import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { readConsent, saveConsent, OPEN_SETTINGS_EVENT } from "../lib/consent";

const CATEGORIES = [
  { key: "analytics", title: "Analytics", text: "Microsoft Clarity and Google Analytics help us see which pages are used and where people get stuck. Never on booking, contact or account pages." },
  { key: "ads", title: "Advertising", text: "Google Ads tells us which of our adverts bring visitors to the site. Never on booking, contact or account pages, and never used to show you personalised adverts." },
];

const Btn = ({ onClick, testId, children }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="flex-1 min-w-[8rem] px-5 py-2.5 rounded-sm bg-bronze-dark text-white text-sm font-medium hover:bg-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-bronze-dark transition-colors">
    {children}
  </button>
);

const Preferences = ({ open, onOpenChange }) => {
  const [c, setC] = useState({ analytics: false, ads: false });
  useEffect(() => { if (open) setC(readConsent() || { analytics: false, ads: false }); }, [open]);
  const done = (v) => { saveConsent(v); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="cookie-preferences-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Cookie settings</DialogTitle>
          <DialogDescription>Choose which optional cookies we may use. You can change this at any time from &quot;Cookie settings&quot; at the bottom of every page.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-4 items-start">
            <div className="flex-1"><p className="font-medium text-sm">Essential</p>
              <p className="text-sm text-ink-muted">Keep you signed in, protect the site and remember this choice. Always on.</p></div>
            <Switch checked disabled aria-label="Essential cookies, always on" data-testid="cookie-switch-essential" />
          </div>
          {CATEGORIES.map((x) => (
            <div key={x.key} className="flex gap-4 items-start">
              <div className="flex-1"><label htmlFor={`cookie-${x.key}`} className="font-medium text-sm">{x.title}</label>
                <p className="text-sm text-ink-muted">{x.text}</p></div>
              <Switch id={`cookie-${x.key}`} checked={c[x.key]} onCheckedChange={(v) => setC({ ...c, [x.key]: v })} data-testid={`cookie-switch-${x.key}`} />
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-3 pt-2">
          <Btn onClick={() => done({ analytics: false, ads: false })} testId="cookie-prefs-reject-all">Reject all</Btn>
          <Btn onClick={() => done({ analytics: true, ads: true })} testId="cookie-prefs-accept-all">Accept all</Btn>
          <button type="button" onClick={() => done(c)} data-testid="cookie-prefs-save"
            className="w-full px-5 py-2.5 rounded-sm border border-bronze-dark text-bronze-dark text-sm font-medium hover:bg-cream-100">Save my choices</button>
        </div>
        <Link to="/cookies" onClick={() => onOpenChange(false)} className="text-sm underline text-bronze-dark" data-testid="cookie-prefs-policy-link">Read our cookie policy</Link>
      </DialogContent>
    </Dialog>
  );
};

export const CookieConsent = () => {
  const { pathname } = useLocation();
  const [chosen, setChosen] = useState(() => !!readConsent());
  const [prefs, setPrefs] = useState(false);
  useEffect(() => {
    const f = () => setPrefs(true);
    window.addEventListener(OPEN_SETTINGS_EVENT, f);
    return () => window.removeEventListener(OPEN_SETTINGS_EVENT, f);
  }, []);
  const decide = (v) => { saveConsent(v); setChosen(true); };
  const onPrefs = (o) => { setPrefs(o); if (!o) setChosen(!!readConsent()); };
  const showBanner = !chosen && !pathname.startsWith("/admin");
  return (
    <>
      {showBanner && !prefs && (
        <div data-testid="cookie-consent-banner" role="region" aria-label="Cookie choice"
          className="fixed bottom-4 left-4 right-4 sm:right-auto sm:max-w-md z-[60] bg-white border border-bronze-light/50 shadow-xl rounded-md p-5">
          <p className="text-sm text-ink">
            We use essential cookies to run this site. With your permission we&apos;d also like to use analytics and advertising cookies. They are never used on booking, contact or account pages.{" "}
            <Link to="/cookies" className="text-bronze-dark underline" data-testid="cookie-banner-policy-link">Cookie policy</Link>
          </p>
          <div className="mt-4 flex gap-3">
            <Btn onClick={() => decide({ analytics: true, ads: true })} testId="cookie-accept-btn">Accept all</Btn>
            <Btn onClick={() => decide({ analytics: false, ads: false })} testId="cookie-reject-btn">Reject all</Btn>
          </div>
          <button type="button" onClick={() => setPrefs(true)} data-testid="cookie-manage-btn"
            className="mt-3 text-sm underline underline-offset-4 text-bronze-dark">Manage preferences</button>
        </div>
      )}
      <Preferences open={prefs} onOpenChange={onPrefs} />
    </>
  );
};
